import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import {
  createAttestation,
  tinLast4,
  type EvaluationType,
  type CategoriesSubmitted,
} from "@/lib/attestations";
import { ArrowLeft, FileSignature } from "lucide-react";

function formatDateLong(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function SubmitCMSAttestation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedPractice } = useAuth();
  const { programYear: contextYear } = useProgramYear();

  // Parse URL params
  const modeParam = searchParams.get("mode");
  const mode: EvaluationType = modeParam === "NPI" ? "NPI" : "GROUP";
  const yearParam = parseInt(searchParams.get("year") || "", 10);
  const programYear = Number.isFinite(yearParam) ? yearParam : contextYear;
  const npiFromUrl = searchParams.get("npi") || "";
  const clinicianFromUrl = searchParams.get("clinician") || "";

  const categories: CategoriesSubmitted = useMemo(
    () => ({
      data_freeze: true,
      quality: searchParams.get("qual") === "1",
      ia: searchParams.get("ia") === "1",
      pi: searchParams.get("pi") === "1",
    }),
    [searchParams]
  );

  // Auto-filled identity
  const entityName = selectedPractice?.tin_name || "";
  const last4 = tinLast4(selectedPractice?.tin);

  // Editable fields (signature + date + title + optional clinician overrides)
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signedDate, setSignedDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // NPI mode editable identity (auto-filled from URL; user may correct)
  const [npiValue, setNpiValue] = useState(npiFromUrl);
  const [clinicianName, setClinicianName] = useState(clinicianFromUrl);

  useEffect(() => {
    setNpiValue(npiFromUrl);
  }, [npiFromUrl]);
  useEffect(() => {
    setClinicianName(clinicianFromUrl);
  }, [clinicianFromUrl]);

  // Auto-populate signature date when name is first typed.
  const trimmedName = signerName.trim();
  useEffect(() => {
    if (trimmedName && !signedDate) {
      setSignedDate(todayIso());
    } else if (!trimmedName && signedDate) {
      setSignedDate("");
    }
  }, [trimmedName, signedDate]);

  // Hard-blocking guards
  if (!selectedPractice) {
    return (
      <div>
        <h1 className="mb-4 text-3xl font-bold">Authorization</h1>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Select a practice to continue.
          </CardContent>
        </Card>
      </div>
    );
  }

  const atLeastOneCategory = categories.quality || categories.ia || categories.pi;
  const selectionsValid = categories.data_freeze && atLeastOneCategory;
  const npiValid = mode === "GROUP" || (npiValue.trim().length > 0);
  const canSubmit =
    selectionsValid &&
    npiValid &&
    trimmedName.length > 0 &&
    signerTitle.trim().length > 0 &&
    signedDate.length > 0 &&
    !submitting;

  const selectedCategoryNames = [
    categories.quality ? "Quality" : null,
    categories.ia ? "Improvement Activities" : null,
    categories.pi ? "Promoting Interoperability" : null,
  ].filter(Boolean) as string[];

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await createAttestation(
        {
          program_year: programYear,
          evaluation_type: mode,
          npi: mode === "NPI" ? npiValue.trim() : null,
          clinician_name: mode === "NPI" ? clinicianName.trim() || null : null,
          entity_name: entityName,
          tin_last4: last4,
          categories_submitted: categories,
          signer_name: trimmedName,
          signer_title_role: signerTitle.trim(),
          signed_date_asserted: signedDate,
        },
        selectedPractice.id
      );
      toast({
        title: "Authorization recorded",
        description: `Signed by ${trimmedName} on ${formatDateLong(signedDate)}.`,
      });
      navigate(`/submit/attestation/${result.attestation.id}`, { replace: true });
    } catch (err) {
      console.error("[SubmitCMSAttestation] submit failed:", err);
      toast({
        title: "Could not record authorization",
        description: err instanceof Error ? err.message : "Unexpected error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="-m-6 min-h-[calc(100vh-3.5rem)] bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-900 dark:to-slate-800">
      <Helmet>
        <title>{`Authorization – ${programYear} MIPS`}</title>
        <link rel="canonical" href="/submit/attestation" />
      </Helmet>

      {/* Component-scoped print/style overrides for the signature font and the
          "looks auto-filled, but editable" date input (matches Registry Avg pattern). */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        .attest-sig-input {
          font-family: 'Dancing Script', Georgia, serif;
          font-size: 22px;
          font-style: normal;
          letter-spacing: 0.01em;
          border: 0;
          border-bottom: 1.5px solid hsl(var(--border));
          border-radius: 0;
          padding: 4px 2px 8px;
          background: transparent;
          box-shadow: none;
          height: auto;
        }
        .attest-sig-input:focus-visible {
          outline: none;
          border-bottom-color: hsl(var(--primary));
          box-shadow: none;
          ring: 0;
        }
        .attest-sig-input::placeholder {
          font-family: -apple-system, system-ui, sans-serif;
          font-size: 13px;
          font-style: normal;
          color: hsl(var(--muted-foreground));
        }
        .attest-auto-input {
          border: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
          padding: 0 !important;
          font: inherit !important;
          line-height: inherit !important;
          height: auto !important;
          min-height: 0 !important;
          -webkit-appearance: none !important;
          appearance: none !important;
        }
      `}</style>

      <Button variant="ghost" size="sm" onClick={() => navigate("/submit")} className="mb-2 -ml-2">
        <ArrowLeft className="mr-1 h-4 w-4" /> Back to Submit to CMS
      </Button>

      <div className="mb-1 flex items-center gap-2">
        <h1 className="text-3xl font-bold">Authorization</h1>
        <Badge variant="secondary">{mode === "GROUP" ? "Group / TIN" : "Individual / NPI"}</Badge>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        Review the authorization below and sign to allow A Value-Based Care Registry to submit your{" "}
        {programYear} MIPS performance data to CMS.
      </p>

      {!selectionsValid && (
        <Card className="mb-4 border-destructive/50 bg-destructive/5">
          <CardContent className="py-4 text-sm">
            Selections are incomplete.{" "}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => navigate("/submit")}
            >
              Return to Submit to CMS
            </Button>{" "}
            to choose what you are submitting.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column: Authorization (legal text) */}
        <div className="space-y-4 lg:col-span-2">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle className="text-base">Authorization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-[15px] leading-7 [&_p+p]:mt-3">
          {mode === "GROUP" ? (
            <>
              <p>
                {entityName || "[Entity Name]"} (TIN ending **-{last4 || "XXXX"}) authorizes A
                Value-Based Care Registry, a CMS-designated Qualified Clinical Data Registry
                (QCDR), to submit {programYear} MIPS performance data to the Centers for Medicare
                &amp; Medicaid Services (CMS) on its behalf.
              </p>
              <p>
                This authorization covers submission of performance data for all patients
                regardless of payer across the selected MIPS performance categories and measures
                for the {programYear} performance year, in accordance with 42 CFR § 414.1400.
              </p>
              <p>
                The signatory represents that they hold full authority to execute this authorization
                on behalf of the identified entity.
              </p>
            </>
          ) : (
            <>
              <p>
                {clinicianName || "[Clinician Name]"} (NPI: {npiValue || "[NPI]"}), affiliated with{" "}
                {entityName || "[Entity Name]"} (TIN ending **-{last4 || "XXXX"}), authorizes A
                Value-Based Care Registry, a CMS-designated Qualified Clinical Data Registry
                (QCDR), to submit {programYear} MIPS performance data to the Centers for Medicare
                &amp; Medicaid Services (CMS) on their behalf.
              </p>
              <p>
                This authorization covers submission of performance data for all patients
                regardless of payer across the selected MIPS performance categories and measures
                for the {programYear} performance year, in accordance with 42 CFR § 414.1400.
              </p>
              <p>
                {clinicianName || "[Clinician Name]"} represents that they are the eligible
                clinician identified herein and holds full authority to execute this authorization.
              </p>
            </>
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
            Certification: By signing below, the {mode === "GROUP" ? "signatory" : "clinician"}{" "}
            certifies that all data submitted to CMS{" "}
            {mode === "GROUP" ? "on behalf of this entity" : "on their behalf"} is true, accurate,
            and complete to the best of their knowledge. If any submitted information is found to
            be inaccurate or incomplete, corrected information will be provided promptly. The{" "}
            {mode === "GROUP" ? "signatory" : "clinician"} understands that the knowing omission,
            misrepresentation, or falsification of any submitted information may be subject to
            criminal, civil, or administrative penalties, including fines, civil damages, and/or
            imprisonment, under applicable federal law.
          </div>

          <div className="max-w-prose border-l-2 border-muted pl-3 text-xs text-muted-foreground">
            Record retention: Per 42 CFR § 414.1400(b)(3)(xiii)(C), this authorization will be
            retained by A Value-Based Care Registry for six (6) years following the {programYear}{" "}
            performance year.
          </div>
        </CardContent>
      </Card>
        </div>

        {/* Right column: Reporting entity + Submission scope */}
        <div className="space-y-4 lg:col-span-1 lg:sticky lg:top-4 lg:self-start">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Reporting entity</CardTitle>
            </CardHeader>
            <CardContent>
              {mode === "GROUP" ? (
                <div className="space-y-3">
                  <FieldAutoFilled label="Legal entity name" value={entityName} />
                  <FieldAutoFilled label="TIN (last 4)" value={last4} />
                </div>
              ) : (
                <div className="space-y-3">
                  <FieldAutoFilledOrEditable
                    label="Clinician name"
                    value={clinicianName}
                    onChange={setClinicianName}
                    placeholder="Clinician full name"
                  />
                  <FieldAutoFilledOrEditable
                    label="NPI"
                    value={npiValue}
                    onChange={setNpiValue}
                    placeholder="10-digit NPI"
                    inputMode="numeric"
                  />
                  <FieldAutoFilled label="Legal entity name" value={entityName} />
                  <FieldAutoFilled label="TIN (last 4)" value={last4} />
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Submission scope</CardTitle>
              <CardDescription>
                Confirmed on Submit to CMS. Return to that step to change.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Data Freeze:</span>{" "}
                <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
                  Acknowledged
                </Badge>
              </div>
              <div>
                <span className="font-medium">Categories:</span>{" "}
                {selectedCategoryNames.length > 0 ? (
                  <span>{selectedCategoryNames.join(", ")}</span>
                ) : (
                  <span className="text-destructive">None selected</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Signature — prominent, full-width, document-style action area */}
      <Card className="mt-6 border-2 border-primary/40 shadow-lg">
        <CardHeader className="border-b bg-primary/5">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSignature className="h-5 w-5 text-primary" /> Signature
          </CardTitle>
          <CardDescription>
            You are signing this authorization as the {mode === "GROUP" ? "entity representative" : "eligible clinician"}.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <Label className="mb-1 block text-xs text-muted-foreground" htmlFor="sig-name">
                Type full name to sign
              </Label>
              <Input
                id="sig-name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Your name"
                className="attest-sig-input"
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="mb-1 block text-xs text-muted-foreground" htmlFor="sig-title">
                Title / role
              </Label>
              <Input
                id="sig-title"
                value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                placeholder={
                  mode === "GROUP" ? "e.g. Chief Executive Officer" : "e.g. Eligible Clinician"
                }
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="mb-1 block text-xs text-muted-foreground" htmlFor="sig-date">
                Date signed{" "}
                <span className="text-[10px] text-muted-foreground/70">
                  (auto-filled from signature)
                </span>
              </Label>
              <Input
                id="sig-date"
                type="date"
                value={signedDate}
                onChange={(e) => setSignedDate(e.target.value)}
                className="attest-auto-input border-b border-input pb-1.5 text-sm"
                disabled={!trimmedName}
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t pt-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/submit")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit} size="lg">
              {submitting ? "Recording…" : "Sign and submit authorization"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FieldAutoFilled({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">
        {label} <span className="text-[10px] text-muted-foreground/70">(auto-filled)</span>
      </div>
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function FieldAutoFilledOrEditable({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: "numeric" | "decimal" | "text";
}) {
  // Looks auto-filled (muted background, no visible border) but is editable.
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">
        {label} <span className="text-[10px] text-muted-foreground/70">(auto-filled)</span>
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className="h-auto rounded-md border bg-muted/40 px-3 py-2 text-sm shadow-none focus-visible:ring-1"
      />
    </div>
  );
}
